import { Box, Chip, Typography } from "@mui/material";
import React from "react";
import { MainEntity } from "../../../types/entityTypes";
import { getDisplayNamesFromDbValues } from "../../../utils/facilityTypeMapping";
import { getLabelFullName } from "../../../utils/labelMapping";
import { getDisplayNamesFromDbValues as getOrgCategoryDisplayNames } from "../../../utils/organizationCategoryMapping";
import { projectTypeMapping } from "../../../utils/projectTypeMapping";

interface EntityContentHeaderProps {
  mainEntity: MainEntity | null;
}

/**
 * Content header with entity name, description, and metadata chips
 * Can be constrained to a specific width
 */
const EntityContentHeader: React.FC<EntityContentHeaderProps> = ({
  mainEntity,
}) => {
  return (
    <Box sx={{ pt: 3, pb: 0, pl: 5, pr: 4, flexShrink: 0 }}>
      <Typography variant="h4" gutterBottom>
        {mainEntity?.name || "Unknown Entity"}
      </Typography>

      {/* Native name display for organizations and facilities */}
      {(mainEntity?.entityType === "organization" ||
        mainEntity?.entityType === "facility") &&
        mainEntity?.name_native &&
        mainEntity.name_native !== mainEntity.name && (
          <Typography
            variant="body2"
            color="text.secondary"
            sx={{
              fontSize: "0.875rem",
              fontStyle: "italic",
              mt: -1,
              mb: 1,
            }}
          >
            {mainEntity.name_native}
          </Typography>
        )}

      <Box sx={{ mb: 3 }}>
        {/* Entity Type Chips - Show different fields based on entity type */}
        {mainEntity?.entityType === "facility" &&
          mainEntity.facility_type &&
          getDisplayNamesFromDbValues(mainEntity.facility_type).map(
            (type: string, index: number) => (
              <Chip
                key={`facility-type-${index}`}
                label={type}
                variant="filled"
                sx={{ mr: 1, mb: 0.5 }}
              />
            )
          )}
        {mainEntity?.entityType === "project" &&
          mainEntity.project_type &&
          mainEntity.project_type.map((type: string, index: number) => {
            let displayName: string;
            try {
              displayName = projectTypeMapping.getDisplayName(type);
            } catch {
              displayName = type;
            }

            return (
              <Chip
                key={`project-type-${index}`}
                label={displayName}
                variant="outlined"
                sx={{ mr: 1, mb: 0.5 }}
              />
            );
          })}
        {/* Organization Category Chips */}
        {mainEntity?.entityType === "organization" &&
          mainEntity.organization_type &&
          getOrgCategoryDisplayNames(mainEntity.organization_type).map(
            (category: string, index: number) => (
              <Chip
                key={`org-category-${index}`}
                label={category}
                variant="filled"
                sx={{ mr: 1, mb: 0.5 }}
              />
            )
          )}
        {/* Organization Labels - Outlined chips */}
        {mainEntity?.entityType === "organization" &&
          mainEntity.labels &&
          mainEntity.labels.map((label: string, index: number) => (
            <Chip
              key={`org-label-${index}`}
              label={getLabelFullName(label)}
              variant="outlined"
              sx={{ mr: 1, mb: 0.5 }}
            />
          ))}
        {/* Labels - Outlined chips for facilities */}
        {mainEntity?.entityType === "facility" &&
          mainEntity.labels &&
          mainEntity.labels.map((label: string, index: number) => (
            <Chip
              key={`label-${index}`}
              label={getLabelFullName(label)}
              variant="outlined"
              sx={{ mr: 1, mb: 0.5 }}
            />
          ))}
      </Box>

      <Typography
        variant="body1"
        color="text.secondary"
        paragraph
        marginBottom={0}
      >
        {mainEntity?.entityType === "source"
          ? mainEntity?.summary || "No summary available"
          : mainEntity?.description || "No description available"}
      </Typography>

      {/* Keywords for projects */}
      <Box sx={{ mt: 2 }}>
        {mainEntity?.entityType === "project" &&
          mainEntity.keywords &&
          mainEntity.keywords.map((keyword: string, index: number) => (
            <Chip
              key={`keyword-${index}`}
              label={keyword}
              variant="outlined"
              size="small"
              sx={{
                mr: 1,
                mb: 0.5,
                color: "primary.contrastText",
                "&:hover": {
                  backgroundColor: "primary.dark",
                },
              }}
            />
          ))}
      </Box>
    </Box>
  );
};

export default EntityContentHeader;
